import {
  resolvePostContentTypes,
  type PostContentType,
} from "@dialogueconsulting/post-content-type";
import type { PostType } from "../../shared/types/post";

/** Structural input shared by calendar, drafts, approvals and post lists. */
export type PostTypeIconInput = {
  type?: PostType | string | null;
  storyType?: string | null;
  publishStoryManually?: boolean | null;
  isVideo?: boolean | null;
  isStory?: boolean | null;
  isFbReel?: boolean | null;
  url?: string | null;
  platformType?: string | null;
  ytTitle?: string | null;
  caption?: string | null;
  postContentType?: PostContentType | null;
  mediaFiles?: ReadonlyArray<{ isVideo?: boolean | null }> | null;
};

export type PostMediaKind =
  "image" | "carousel" | "video" | "text" | "document" | "story";

const icons: Record<PostMediaKind, string> = {
  image: "fa-image",
  carousel: "fa-rectangle-vertical-history",
  video: "fa-clapperboard-play",
  text: "fa-text",
  document: "fa-file-pdf",
  story: "fa-circle-notch",
};

const labels: Record<PostMediaKind, string> = {
  image: "Image",
  carousel: "Carousel",
  video: "Video/Reel",
  text: "Text",
  document: "Document/PDF",
  story: "Story",
};

export const postMediaKindByContentType: Record<
  PostContentType,
  PostMediaKind
> = {
  story: "story",
  reel: "video",
  video: "video",
  image: "image",
  multiMedia: "carousel",
  text: "text",
  document: "document",
};

/** The shared classifier decides; a post it cannot place falls back to its
 * assets. A null server stamp means unknown, so only `undefined` reclassifies. */
export const postMediaKind = (post: PostTypeIconInput): PostMediaKind => {
  const [contentType] = resolvePostContentTypes(post);
  if (contentType) return postMediaKindByContentType[contentType];

  const { type, storyType, isVideo, mediaFiles } = post;
  if (storyType === "reel" || storyType === "igtv") return "video";
  if (storyType === "story") return "story";
  if (type === "carousel") return "carousel";
  if (type === "reel") return "video";
  if ((mediaFiles?.length ?? 0) > 1) return "carousel";
  if (isVideo || mediaFiles?.[0]?.isVideo) return "video";
  return "image";
};

export const postTypeIcon = (post: PostTypeIconInput): string =>
  icons[postMediaKind(post)];

export const postTypeIconClassName = (post: PostTypeIconInput): string => {
  const kind = postMediaKind(post);
  return `fa-regular ${icons[kind]}`;
};

export const postTypeIconLabel = (post: PostTypeIconInput): string =>
  labels[postMediaKind(post)];
